import React from 'react';

import { Common, LayerManager } from 'models';
import { makeWeakRandomId } from 'utils';
import { BfWorkshopId, FaftWorkshopId } from 'models/workflow';

export enum SectionEnum {
    'Assets',
    'Treatments',
    'Risk Results',
}

export interface LayerState {
    id: LayerManager.Layer.Id;
    opacity?: number;
    zIndex?: number;
}

export type ContentType = 'text' | 'image';

export interface ContentItemText {
    type: 'text';
    /**
     * Text to be contained in the element
     */
    text: string;
    /**
     * CSS styles passed to the element.
     */
    style?: React.CSSProperties;
}

export interface ContentItemImage {
    type: 'image';
    /**
     * Src of the image
     */
    src: string;
    /**
     * Is treated by a loadable image slightly differently when
     * a width is explicitly specified over a style.
     * For example it auto centers it.
     */
    width?: React.CSSProperties['width'];
    /**
     * CSS styles passed to the element.
     */
    style?: React.CSSProperties;
}

export type ContentItem = ContentItemText | ContentItemImage;

export interface Step {
    id: string;
    title: string;
    content: string | ContentItem[];
    icon?: string;
    level: number;
    bounds: Common.Bounds;
    basemap?: LayerManager.Layer.Id;
    legend?: LayerManager.Layer.Id;
    layerStates: LayerState[];
}

export type Section = keyof typeof SectionEnum;

export type Guide = Record<Section, Step[]>;

export const sections = Object.keys(SectionEnum).filter((x) =>
    Number.isNaN(+x),
) as Section[];

const sampleGuide: Guide = {
    Assets: [
        {
            id: 'Bldg_Residential',
            title: 'Residential Buildings',
            icon: '/thumbnails/home.svg',
            content: [
                {
                    type: 'text',
                    text: 'Residential areas including urban bushland interface areas and rural properties.',
                },
                {
                    type: 'image',
                    src: '/thumbnails/rfs-house.jpg',
                    width: '50%',
                    style: { textAlign: 'left' },
                },
            ],
            level: 0,
            bounds: {
                minLong: 151.1383,
                minLat: -32.5537,
                maxLong: 151.1674,
                maxLat: -32.5356,
            },
            layerStates: [
                {
                    id: 'Admin_BMC',
                    opacity: 0.5,
                    zIndex: 1,
                },
                {
                    id: 'Bldg_Residential',
                    opacity: 0.8,
                    zIndex: 2,
                },
            ],
        },
        {
            id: 'Bldg_SFPP',
            title: 'Special Fire Protection',
            icon: '/thumbnails/town-hall.svg',
            content: [
                {
                    type: 'text',
                    text: 'Special Fire Protection areas including schools, hospitals, nursing homes, and tourist facilities.',
                },
                {
                    type: 'image',
                    src: '/thumbnails/bldg_sfpp_legend.png',
                    width: '33%',
                },
            ],
            level: 0,
            bounds: {
                minLong: 150,
                minLat: -33.16,
                maxLong: 152.3,
                maxLat: -31.9,
            },
            layerStates: [
                {
                    id: 'Admin_BMC',
                    opacity: 0.5,
                    zIndex: 1,
                },
                {
                    id: 'Bldg_Schools',
                    opacity: 0.8,
                    zIndex: 2,
                },
            ],
        },
        {
            id: 'Rec_CommunityFacility',
            title: 'Community Recreational Facilities',
            icon: '/thumbnails/picnic-site.svg',
            content:
                'Recreation facility means a building or place used predominantly for recreation.',
            level: 0,
            bounds: {
                minLong: 150,
                minLat: -33.16,
                maxLong: 152.3,
                maxLat: -31.9,
            },
            layerStates: [
                {
                    id: 'Admin_BMC',
                    opacity: 0.5,
                    zIndex: 1,
                },
                {
                    id: 'Bldg_Commercial',
                    opacity: 0.8,
                    zIndex: 2,
                },
            ],
        },
        {
            id: 'Rec_HighValueFacility',
            title: 'High Value Recreational Facilities',
            icon: '/thumbnails/museum.svg',
            content:
                'Recreation facility (major) means a building or place used for large-scale sporting or recreation activities that are ' +
                'attended by large numbers of people whether regularly or periodically, and includes theme parks, sports stadiums, ' +
                'showgrounds, racecourses and motor racing tracks.',
            level: 0,
            bounds: {
                minLong: 150.8396,
                minLat: -32.9806,
                maxLong: 152.2436,
                maxLat: -32.215,
            },
            layerStates: [
                {
                    id: 'Admin_BMC',
                    opacity: 0.5,
                    zIndex: 1,
                },
                {
                    id: 'Bldg_Industrial',
                    opacity: 0.8,
                    zIndex: 2,
                },
            ],
        },
        {
            id: makeWeakRandomId(),
            title: 'Agriculture',
            content: [
                {
                    type: 'text',
                    text:
                        'Primary production is one of our biggest industries ' +
                        'and a significant contributor to the SA and national economies. ' +
                        'The industry is highly diverse, including plant ' +
                        'cultivation, rearing animals for meat and other products, ' +
                        ' and production of fish and other water-based species ' +
                        'such as oysters.',
                },
                {
                    type: 'image',
                    src: '/thumbnails/agriculture.png',
                    width: '70%',
                },
            ],
            level: 0,
            bounds: {
                minLong: 150,
                minLat: -33.16,
                maxLong: 152.3,
                maxLat: -31.9,
            },
            layerStates: [
                {
                    id: 'Admin_BMC',
                    opacity: 0.5,
                    zIndex: 1,
                },
            ],
        },
        {
            id: 'Ag_BulkGrainStorageBldg',
            title: 'Bulk Grain Storage Facilities',
            icon: '/thumbnails/warehouse.svg',
            content:
                'Bulk grain storage facility means a building or structure used for the commercial bulk handling or storage of granular materials ' +
                'such as grain, ore, or the like, and includes cell type silos and storage sheds.',
            level: 1,
            bounds: {
                minLong: 150,
                minLat: -33.16,
                maxLong: 152.3,
                maxLat: -31.9,
            },
            layerStates: [
                {
                    id: 'Admin_BMC',
                    opacity: 0.5,
                    zIndex: 1,
                },
                {
                    id: 'Agriculture',
                    opacity: 0.8,
                    zIndex: 2,
                },
            ],
        },
        {
            id: makeWeakRandomId(),
            title: 'Horticulture Areas',
            content:
                'Horticulture industry comprises fruit, vegetables, nuts, flowers, turf and nursery products.',
            level: 1,
            bounds: {
                minLong: 150,
                minLat: -33.16,
                maxLong: 152.3,
                maxLat: -31.9,
            },
            layerStates: [
                {
                    id: 'Admin_BMC',
                    opacity: 0.5,
                    zIndex: 1,
                },
            ],
        },
        {
            id: 'Ag_Horticulture',
            title: 'Vineyards',
            icon: '/thumbnails/ag_horticulturevineyards.png',
            content:
                'Areas containing plantations of grape-bearing vines, grown mainly for winemaking, but also raisins, table grapes and non-alcoholic grape juice.',
            level: 2,
            bounds: {
                minLong: 150,
                minLat: -33.16,
                maxLong: 152.3,
                maxLat: -31.9,
            },
            layerStates: [
                {
                    id: 'Admin_BMC',
                    opacity: 0.8,
                    zIndex: 1,
                },
                {
                    id: 'Ag_Horticulture',
                    opacity: 0.6,
                    zIndex: 2,
                },
            ],
        },
        {
            id: makeWeakRandomId(),
            title: 'Vegetables',
            content:
                'Vegetable production operations range from small patches of crops, producing a few vegetables for family use or marketing, to highly organized and mechanized farms',
            level: 2,
            bounds: {
                minLong: 150,
                minLat: -33.16,
                maxLong: 152.3,
                maxLat: -31.9,
            },
            layerStates: [
                {
                    id: 'Admin_BMC',
                    opacity: 0.8,
                    zIndex: 1,
                },
                {
                    id: 'Ag_Horticulture',
                    opacity: 0.8,
                    zIndex: 2,
                },
            ],
        },
        {
            id: makeWeakRandomId(),
            title: 'Fruit and Nut trees',
            content:
                'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
            level: 2,
            bounds: {
                minLong: 150,
                minLat: -33.16,
                maxLong: 152.3,
                maxLat: -31.9,
            },
            layerStates: [
                {
                    id: 'Admin_BMC',
                    opacity: 0.8,
                    zIndex: 1,
                },
                {
                    id: 'Ag_Horticulture',
                    opacity: 0.8,
                    zIndex: 2,
                },
            ],
        },
        {
            id: makeWeakRandomId(),
            title: 'Agriculture: Livestock',
            content: 'This section contains some livestock layers',
            level: 1,
            bounds: {
                minLong: 150,
                minLat: -33.16,
                maxLong: 152.3,
                maxLat: -31.9,
            },
            layerStates: [
                {
                    id: 'Admin_BMC',
                    opacity: 0.5,
                    zIndex: 1,
                },
            ],
        },
        {
            id: makeWeakRandomId(),
            title: 'Intensive Animal Production',
            content:
                'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
            level: 2,
            bounds: {
                minLong: 150,
                minLat: -33.16,
                maxLong: 152.3,
                maxLat: -31.9,
            },
            layerStates: [
                {
                    id: 'Admin_BMC',
                    opacity: 0.8,
                    zIndex: 1,
                },
                {
                    id: 'Ag_Poultry',
                    opacity: 0.8,
                    zIndex: 2,
                },
            ],
        },
        {
            id: makeWeakRandomId(),
            title: 'Livestock Infrastructure',
            content: 'Here are a few more layers',
            level: 2,
            bounds: {
                minLong: 150,
                minLat: -33.16,
                maxLong: 152.3,
                maxLat: -31.9,
            },
            layerStates: [
                {
                    id: 'Admin_BMC',
                    opacity: 0.8,
                    zIndex: 1,
                },
                {
                    id: 'Ag_Livestock',
                    opacity: 0.6,
                    zIndex: 2,
                },
                {
                    id: 'Agriculture',
                    opacity: 0.8,
                    zIndex: 5,
                },
            ],
        },
    ],
    Treatments: [],
    'Risk Results': [
        {
            id: makeWeakRandomId(),
            title: 'Burn Frequency',
            content:
                'This is meant to be a larger title containing the next two as children',
            level: 0,
            bounds: {
                minLong: 150,
                minLat: -33.16,
                maxLong: 152.3,
                maxLat: -31.9,
            },
            layerStates: [
                {
                    id: 'Admin_BMC',
                    opacity: 0.5,
                    zIndex: 1,
                },
            ],
        },
    ],
};

export const guides: Record<BfWorkshopId | FaftWorkshopId, Guide> = {
    bf_workshop_1: sampleGuide,
    bf_workshop_2: sampleGuide,
    bf_workshop_3: sampleGuide,
    bf_workshop_4: sampleGuide,
    faft_pre_workshop: sampleGuide,
    faft_workshop_1: sampleGuide,
    faft_workshop_2_3: sampleGuide,
    faft_workshop_4: sampleGuide,
};

export type Comment = {
    id: string;
    parentId?: string;
    userId: string;
    upVotes: number;
    downVotes: number;
    userName: string;
    text: string;
    replies: Comment[];
};

export type LayerReview = {
    comments: Comment[];
};

export type LayerReviewControl = {
    enabled: boolean;
    type: 'add' | 'edit' | 'delete' | '';
};

export type LayerReviewModal = {
    stepId: string;
    title: string;
    isOpen: boolean;
};
